/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * G&M 04/09 (K8) — KAP PAA DEN SELVHELENDE VAGT. Se den fulde begrundelse oeverst i
 * assets/cart-items.js. Kort: `dispatch:cart-drawer:refresh` er et OFFENTLIGT
 * integrationspunkt (assets/custom.js, punkt 12), handleren faar Event-objektet som
 * `dontRefreshCartItems` (altid truthy) og endte derfor ALTID i `renderContents` =>
 * ALTID vagten. En app-embed (Drip/Selecty — begge aktive, begge UMAALTE) der fyrer
 * eventet paa interval, ville give hver godkendt engroskunde uendelig genindlaesning.
 *
 * Funktionen findes i BEGGE filer med samme krop: `cart-items.js` indlaeses dynamisk af
 * `cart-drawer.js`, og paa /cart indlaeses den uden drawer'en overhovedet. En delt
 * global ville derfor afhaenge af en indlaesningsraekkefoelge der ikke er garanteret.
 * En genangivelse af samme funktion i klassisk script er ufarlig.
 *
 * ⛔ FEJLER LUKKET: uden sessionStorage kan forsoeg ikke taelles, og saa genindlaeses
 * der ikke. En utaellelig genindlaesning ER loekken.
 *
 * ⚠ EEN NOEGLE PR. AARSAG ('vagt' / 'sektioner') — se den fulde begrundelse i
 * assets/cart-items.js. Kort: en netvaerksfejl paa sektions-hentningen maa ikke bruge den
 * genindlaesning, pris-vagten skulle have haft.
 *
 * ⛔ G&M 04/09 (K9): ukendt aarsagsnavn ⇒ ingen genindlaesning. Se assets/cart-items.js.
 *
 * ⛔ BEGGE FUNKTIONER SKAL VAERE TEGN FOR TEGN ENS I DE TO FILER. Det er ikke en
 * hoeflighed: divergerer de, opfoerer kurven sig forskelligt paa /cart og i drawer'en,
 * TAVST. `vaerktoej/skriv_kurv.py` hard-fejler nu, hvis kroppene ikke er identiske —
 * K8 udpegede selv duplikeringen som sit stoerste egen-angreb og lod porten vaere.
 *
 * ⛔⛔ G&M 04/09 (K10) — DET ABSOLUTTE SESSIONSLOFT (`gmK7ReloadIAlt`). K9 lukkede IKKE
 * loekken; den FLYTTEDE den fra 'sektioner'-noeglen til 'vagt'-noeglen. Veksler
 * pris-tilstanden mens sektionssvaret er PERFEKT, gen-armerer en god gentegning
 * reload'et, og den naeste daarlige bruger det: 4 haendelser => 2 genindlaesninger,
 * 12 => 6 — ogsaa via det OFFENTLIGE `dispatch:cart-drawer:refresh`. Loftet taeller
 * ADFAERDEN (genindlaesninger pr. faneblad) i stedet for at lappe PLACERINGEN (hvilken
 * gren rydder hvad), og ryddes ALDRIG af `gmRydReloadKap`.
 * Den fulde begrundelse staar oeverst i assets/cart-items.js.
 *
 * @param {string} aarsag - 'vagt' eller 'sektioner'. Bliver til noeglens navn.
 * @returns {boolean} true hvis der BLEV genindlaest (kalderen skal returnere).
 */
function gmReloadEnGang(aarsag) {
  if (aarsag !== 'vagt' && aarsag !== 'sektioner') return false;
  try {
    if (window.sessionStorage.getItem(`gmK7Reload:${aarsag}`)) return false;
    const ialt = Number(window.sessionStorage.getItem('gmK7ReloadIAlt') || 0);
    if (!(ialt < 2)) return false;
    window.sessionStorage.setItem('gmK7ReloadIAlt', String(ialt + 1));
    window.sessionStorage.setItem(`gmK7Reload:${aarsag}`, String(Date.now()));
  } catch (e) {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * ⛔ G&M 04/09 (K9) — RYDDER EEN KAP, IKKE BEGGE. K8 ryddede begge noegler i en gren der
 * kun betyder «vagten fyrede ikke». En TOM gentegning (manglende sektionsnoegle ⇒ tom
 * streng fra getElementHTML) har ingen `.gm-plock`, faldt derfor i den gren og ryddede
 * kappen — og en VEKSLENDE sektionsrespons blev en uendelig loekke, een genindlaesning
 * pr. to haendelser, uden en kundehandling imellem. Se den fulde begrundelse oeverst i
 * assets/cart-items.js. En aarsag ryddes nu kun paa en MAALT god gentegning.
 * Fejler lukket i begge retninger.
 *
 * @param {string} aarsag - 'vagt' eller 'sektioner'.
 */
function gmRydReloadKap(aarsag) {
  if (aarsag !== 'vagt' && aarsag !== 'sektioner') return;
  try {
    window.sessionStorage.removeItem(`gmK7Reload:${aarsag}`);
  } catch (e) {
    /* fejler lukket — kappen forbliver sat */
  }
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          // ⛔ G&M 04/09 (K9): `await` tilfoejet. Uden det kunne `try/catch`'en ovenfor
          // ikke fange en afvisning fra `refresh()` — den blev en uhaandteret afvisning,
          // og catch-blokken var en attrap. Eet ord; den goer porten til det den udgiver
          // sig for at vaere.
          await cartItems.refresh();
        } else {
          // G&M 04/09 (K7): renderContents henter selv sektionerne med et
          // storefront-GET — se kommentaren dér.
          await this.renderContents(null, false);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        // ⛔ G&M 04/09 (K8): her stod `this.errorMsg.textContent`. `errorMsg` saettes
        // ALDRIG paa CartDrawer (kun paa ProductForm) — det var altid `undefined`, saa
        // selve FEJLVEJEN kastede en TypeError, og `on:cart:error` naaede aldrig at
        // blive fyret. En fejlvej der selv gaar i stykker, er ingen fejlvej. Denne
        // metode kaldes desuden fra en event-lytter uden `catch`, saa kastet blev en
        // uhaandteret afvisning. Vi bruger temaets egen kurvfejl-streng.
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: theme.strings.cartError
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      // ⛔ G&M 04/09 (K7): sektions-HTML hentes ALTID med et almindeligt
      // storefront-GET (?sections=) — ALDRIG fra `data.sections` i svaret paa
      // POST /cart/add.js eller /cart/change.js. Den indlejrede vej renderes i en
      // anden kontekst, hvor pris-gaten i snippets/price-as-money.liquid faldt
      // lukket for godkendte engroskunder. Se assets/cart-items.js for hele
      // begrundelsen. Parameteren `data` bevares for kaldekompatibilitet, men
      // dens `sections` bruges bevidst IKKE.
      //
      // ⛔ G&M 04/09 (K8): `!ok` faar KUN HTTP-status. Et netvaerksglip AFVISER
      // `fetch` uden nogensinde at naa `.ok`; en Cloudflare-udfordring svarer HTTP 200
      // med HTML (maalt af K7, §8.1) saa `.json()` kaster. Denne metode kaldes fra
      // `product-form.js` INDE i et `setTimeout(…, 700)` UDEN `await` og UDEN `catch`
      // — en afvist promise blev derfor en UHAANDTERET afvisning: varen ER i kurven paa
      // serveren, drawer'en aabner aldrig, og kunden faar intet at vide. Foer K7's
      // rettelse kunne det ikke ske. Enhver fejl behandles derfor som `!ok`.
      const sectionIds = this.getSectionsToRender().map((section) => section.section);
      let sections = null;
      try {
        const sectionsResponse = await fetch(`?sections=${sectionIds.join(',')}`);
        if (!sectionsResponse.ok) throw new Error(String(sectionsResponse.status));
        sections = await sectionsResponse.json();
      } catch (sektionsFejl) {
        // Kurven ER aendret paa serveren. Hellere en fuld, server-renderet
        // genindlaesning end en delvis gentegning der kan vise et forkert beloeb —
        // men HOEJST EEN gang (se gmReloadEnGang oeverst i filen).
        if (gmReloadEnGang('sektioner')) return;

        // ⛔ Kappen er brugt. Vi maa hverken genindlaese igen ELLER kaste: kalderen i
        // `product-form.js` har hverken `await` eller `catch`, saa et kast bliver en
        // uhaandteret afvisning — praecis den fejl vi lukker. Og vi aabner IKKE en drawer
        // hvis indhold vi ikke kan staa inde for: den ville vise det GAMLE antal og det
        // GAMLE beloeb, mens serveren har det nye.
        console.log(sektionsFejl); // eslint-disable-line

        // ⛔ G&M 04/09 (K8): `on:cart:error` ALENE er IKKE en besked til kunden. Maalt i
        // temaet: INGEN lytter viser noget synligt (`assets/custom.js` naevner kun eventet
        // i sin dokumentation, `assets/theme-editor.js` bruger det kun i editoren). Vi
        // skriver derfor OGSAA i temaets eget fejl-element. ⚠ Er drawer'en lukket, eller
        // findes elementet ikke (tom kurv foer tilfoejelsen), er kundens eneste signal
        // det opdaterede antal i kurv-ikonet — som ProductForm.updateCartIcon allerede
        // har sat korrekt. Det staar aabent i K8-rapportens afsnit
        // «HVAD JEG IKKE LUKKEDE» (henvist ved NAVN, ikke ved nummer: et
        // afsnitsnummer i en kommentar i live-kode raadner ved foerste omnummerering).
        const kurvFejl = document.getElementById('cart-errors');
        if (kurvFejl) {
          kurvFejl.textContent = theme.strings.cartError;
          kurvFejl.hidden = false;
        }

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: theme.strings.cartError
          }
        }));

        // ⛔ G&M 04/09 (K9) — G1: BESKEDEN VAR INERT PRAECIS DÉR HVOR FEJLEN LEVER.
        // Maalt 04/09 i raa HTML fra tre sidetyper (vaerktoej/k9_maaling.py, M2):
        // `#cart-errors` findes én gang pr. side. Paa forside og produktside ligger den
        // inde i <cart-drawer> ⇒ USYNLIG naar drawer'en er lukket. Kun paa /cart ligger
        // den i selve kurv-formularen — og dér findes <cart-drawer> slet ikke (maalt: 0),
        // saa denne kodesti koerer der aldrig. ⇒ Modtraekket skrev sin besked ind i en
        // skjult beholder paa hver eneste side hvor den kunne fyre.
        //
        // AFVEJNINGEN, truffet og ikke gemt vaek: er en synlig fejl over en FORAELDET
        // kurv vaerre end tavshed? Nej — og grunden er maalt, ikke gaettet:
        //   · Kurv-ikonets antal er ALLEREDE korrekt (ProductForm.updateCartIcon koerer
        //     foer denne sti). Aabner drawer'en med sit gamle indhold, ser kunden et
        //     antal der IKKE stemmer med listen, PLUS en fejlbanner. To signaler om at
        //     noget gik galt, og ét af dem er sandt.
        //   · Uden aabning er der ingen synlig forskel paa «kurven blev ikke opdateret»
        //     og «knappen er doed». Det er den staerkest taenkelige invitation til at
        //     klikke igen — og paa en engrosshop er eet ekstra klik een ekstra 30-50 m
        //     rulle. Husets 07/08-fejlklasse: udfoert fra systemets side, brudt fra
        //     kundens.
        //
        // ⛔ MEN KUN NAAR KALDEREN BAD OM DET. `openDrawer` er false, naar
        // `CartDrawer.refresh()` gentegner i baggrunden — og den kaldes af det OFFENTLIGE
        // event `dispatch:cart-drawer:refresh`. At aabne dér ville forvandle en app-embed
        // paa interval til en skuffe der springer op igen og igen: et vaerre problem end
        // det vi loeser. Vi aabner altsaa kun efter en handling kunden selv foretog.
        if (openDrawer && this.getAttribute('open') === null) {
          setTimeout(() => this.open());
        }
        return;
      }

      // ⛔ G&M 04/09 (K9) — MAAL gentegningen. Se den fulde begrundelse i
      // assets/cart-items.js. Kort: `getElementHTML` returnerer siden K8 en TOM streng
      // ved en manglende sektionsnoegle, saa «ingen exception» beviser INTET. Maalt
      // 04/09 (K9/M1): ?sections= svarer altid med et JSON-objekt noeglet paa netop de
      // oenskede navne — reglen er derfor maalbar og ikke et gaet.
      let alleNoeglerTilStede = true;
      let skrevetIndhold = false;
      this.getSectionsToRender().forEach((section) => {
        // ⛔ G&M 04/09 (K10) — G1: MAALINGEN GATEDE IKKE SKRIVNINGEN. Foer satte vi blot
        // `alleNoeglerTilStede = false` og SKREV SAA ALLIGEVEL: `getElementHTML` giver
        // '' ved en manglende noegle, saa `#cart-drawer` blev TOEMT — og fejl-banneret
        // skrives ikke, for det ligger i fetch-catch-grenen. Maalt af roed hat runde 3:
        // drawer-indhold FOER=36 tegn, EFTER=0 tegn, og skuffen sprang op TOM efter
        // kundens eget klik. Varen ER koebt paa serveren. Husets 07/08-fejlklasse
        // ordret: udfoert fra systemets side, brudt fra kundens.
        // ⇒ SPRING SKRIVNINGEN OVER for netop den sektion hvis noegle mangler. Det
        // GAMLE indhold bevares (forkert, men laesbart) frem for at blive erstattet af
        // ingenting. Vagten og loftet tager den derfra.
        if (typeof sections[section.section] !== 'string') {
          alleNoeglerTilStede = false;
          return;
        }

        const el = document.getElementById(section.id);
        if (el) {
          const html = CartDrawer.getElementHTML(
            sections[section.section],
            section.selector
          );
          if (html) skrevetIndhold = true;
          el.innerHTML = html;
        }
      });
      const gentegningOK = alleNoeglerTilStede && skrevetIndhold;

      // ⭐ G&M 04/09 (K7) — SELVHELENDE VAGT, samme regel som i assets/cart-items.js.
      // ⛔ G&M 04/09 (K8): med KAP. Se gmReloadEnGang oeverst i filen.
      // ⛔ G&M 04/09 (K9): og med en MAALT ryddeside. Foer ryddede `else`-grenen BEGGE
      // kapper, selv om den kun betyder «vagten fyrede ikke» — en tom gentegning har
      // ingen `.gm-plock` og ryddede derfor kappen som om alt var i orden. Med en
      // vekslende sektionsrespons blev det en uendelig loekke.
      if (gentegningOK) gmRydReloadKap('sektioner');

      if (theme.gmGodkendt && document.querySelector('#cart-drawer .gm-plock')) {
        if (gmReloadEnGang('vagt')) return;
        // Kappen er brugt: vi genindlaeser IKKE igen. Drawer'en aabnes nedenfor med
        // login-CTA i stedet for beloeb — synligt forkert, men ikke en loekke, og
        // kunden faar en kvittering paa at varen er lagt i kurven.
      } else if (theme.gmGodkendt === true && gentegningOK) {
        // ⛔ `=== true`: for en anonym kan vagten aldrig fyre, saa en rydning her ville
        // vaere en rydning uden en maaling bag sig.
        gmRydReloadKap('vagt');
      }

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      window.initLazyImages();
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      // ⛔ G&M 04/09 (K8): manglede sektionsnoeglen i svaret (eller matchede selektoren
      // ikke), gav `querySelector(...).innerHTML` en TypeError midt i gentegningen.
      // `CartItems.getElementHTML` har allerede praecis dette vaern; de to var uden
      // grund forskellige. ⚠ Prisen for vaernet er en TOM sektion i stedet for et kast
      // — bemaerk at den selvhelende vagt IKKE kan se en tom drawer (der er ingen
      // `.gm-plock` at finde). Det staar i K8-rapporten som «ikke lukket».
      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
